/**
 * Credential type mapping configuration
 */
const CREDENTIAL_TYPE_MAP: Record<string, string | null> = {
  // OAuth types → base provider type
  SalesforceAuthCode: 'Salesforce',
  CanvasOAuth: 'Canvas',
  DocuSignOAuth: 'DocuSign',
  ZoomOAuth: 'Zoom',
  GoogleOAuth: 'Google',

  // System types → null (filtered out)
  AdapterSecret: null,
  EnvironmentVariables: null,
};

/**
 * Normalize credential type names to match provider types
 * Matches dataProtect environmentsOG.js normalizeCredentialTypeNames() (lines 58-63)
 *
 * This handles OAuth credential types like "SalesforceAuthCode" which need to map
 * to the base provider type "Salesforce" when looking up provider config.
 */
export const normalizeCredentialType = (credentialType: string): string | null => {
  if (credentialType in CREDENTIAL_TYPE_MAP) {
    return CREDENTIAL_TYPE_MAP[credentialType];
  }
  return credentialType;
};

/**
 * Map provider.type → ProviderLabel used by the backend when storing OAuth credentials.
 *
 * The backend OAuth handlers (e.g. salesforce_agent) store credentials with a ProviderLabel
 * that differs from the connector's provider.type. The checkOAuthCreds API route
 * ({providerLabel} in the URL) matches on this stored ProviderLabel.
 *
 * Only connectors where provider.type !== stored ProviderLabel need an entry here.
 * All others pass through as-is.
 */
const OAUTH_PROVIDER_LABEL_MAP: Record<string, string> = {
  SalesforceRestAPI: 'Salesforce',
};

export const getOAuthProviderLabel = (providerType: string): string => {
  return OAUTH_PROVIDER_LABEL_MAP[providerType] ?? providerType;
};

/**
 * Map provider.type → CredentialType stored by the backend OAuth handlers.
 *
 * The sfAgent stores all Salesforce credentials with credentialType "SalesforceAuthCode",
 * regardless of whether the connector is "Salesforce" or "SalesforceRestAPI".
 * The save (PUT) must use this exact credentialType so transformer_api's
 * FindCredentialByCredentialTypeAndKeyAndEnv finds and updates the SAME record
 * rather than creating a duplicate.
 */
const OAUTH_CREDENTIAL_TYPE_MAP: Record<string, string> = {
  Salesforce: 'SalesforceAuthCode',
  SalesforceRestAPI: 'SalesforceAuthCode',
};

export const getOAuthCredentialType = (providerType: string): string | undefined => {
  return OAUTH_CREDENTIAL_TYPE_MAP[providerType];
};
